import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { api, apiError } from "../lib/api";
import { openPrintWindow, esc } from "../lib/print";
import { PageHeader, Card, Btn, Empty, Table, Td } from "../components/UI";
import { FLabel, FInput, FSelect, FTextarea, CheckBox } from "./engagement/FormControls";
import { SlidePanel, ConfirmDialog } from "../components/SlidePanel";
import { useClients, ClientSelect } from "./clinical/shared";
import { Plus, Trash2, Printer, Bell, StickyNote, FileText, Send, Check, Pencil } from "lucide-react";
import { toast } from "sonner";

const TABS = [["alerts", "Alerts"], ["notify", "Client Notifications"], ["memos", "Memos"], ["templates", "Letter Templates"], ["letters", "Generated Letters"], ["fax", "Fax Cover Sheets"]];
const SEV_TONE = { info: "bg-[#E3F2FD] text-[#1565C0]", warning: "bg-[#FFF3E0] text-[#B84A00]", critical: "bg-[#FFEBEE] text-[#C62828]" };
const ROLES = [["all", "Everyone"], ["admin", "Admins"], ["clinician", "Clinicians"], ["front_desk", "Front Desk"], ["billing", "Billing"]];

function AlertsTab() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ title: "", body: "", severity: "info", audience: "all", category: "system" });
  const [panelOpen, setPanelOpen] = useState(false);
  const [delRow, setDelRow] = useState(null);
  const load = () => { api.get("/alerts").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))); };
  useEffect(load, []);

  const create = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    try { await api.post("/alerts", form); toast.success("Alert published"); setPanelOpen(false); setForm({ title: "", body: "", severity: "info", audience: "all", category: "system" }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async () => {
    try { await api.delete(`/alerts/${delRow.id}`); toast.success("Deleted"); setDelRow(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!data) return <div className="py-12 text-center text-sm text-[#84675B]">Loading alerts…</div>;
  return (
    <div data-testid="alerts-tab">
      <div className="mb-4 flex items-center justify-between">
        <span className="text-sm text-[#7B6157]">{data.unread_count} unread · alerts appear under the bell icon for the targeted audience</span>
        <Btn onClick={() => setPanelOpen(true)} data-testid="new-alert-btn"><Plus size={15} /> Publish Alert</Btn>
      </div>
      {data.alerts.length === 0 ? <Empty text="No alerts published yet." /> : (
        <div className="space-y-2">
          {data.alerts.map((a) => (
            <Card key={a.id} className="flex items-start gap-3 p-4" data-testid={`alert-row-${a.id}`}>
              <span className={`mt-0.5 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase ${SEV_TONE[a.severity]}`}>{a.severity}</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-[#3E2723]">{a.title}</div>
                {a.body && <div className="text-sm text-[#5D4037]">{a.body}</div>}
                <div className="mt-1 text-[11px] text-[#84675B]">To: {(ROLES.find(([k]) => k === a.audience) || ["", a.audience])[1]} · by {a.created_by} · {dayjs(a.created_at).format("MMM D, HH:mm")}</div>
              </div>
              <button data-testid={`alert-delete-${a.id}`} onClick={() => setDelRow(a)} className="rounded-lg p-1.5 text-[#C62828] hover:bg-[#FFEBEE]"><Trash2 size={15} /></button>
            </Card>
          ))}
        </div>
      )}
      <SlidePanel open={panelOpen} onClose={() => setPanelOpen(false)} title="Publish Alert" testId="alert-panel">
        <div className="space-y-4">
          <div><FLabel>Title *</FLabel><FInput data-testid="alert-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
          <div><FLabel>Message</FLabel><FTextarea data-testid="alert-body-input" rows={3} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><FLabel>Severity</FLabel>
              <FSelect data-testid="alert-severity-select" value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })} placeholderDisabled>
                <option value="info">Info</option><option value="warning">Warning</option><option value="critical">Critical</option>
              </FSelect></div>
            <div><FLabel>Audience</FLabel>
              <FSelect data-testid="alert-audience-select" value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} placeholderDisabled>
                {ROLES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </FSelect></div>
          </div>
          <div className="flex justify-end gap-2"><Btn variant="secondary" onClick={() => setPanelOpen(false)}>Cancel</Btn>
            <Btn onClick={create} data-testid="alert-publish-btn"><Send size={14} /> Publish</Btn></div>
        </div>
      </SlidePanel>
      <ConfirmDialog open={!!delRow} onClose={() => setDelRow(null)} onConfirm={remove} description={`Delete alert "${delRow?.title}"?`} />
    </div>
  );
}

function MemosTab() {
  const [rows, setRows] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [form, setForm] = useState({ subject: "", body: "", priority: "normal" });
  const [delRow, setDelRow] = useState(null);
  const load = () => { api.get("/memos").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e))); };
  useEffect(load, []);

  const create = async () => {
    if (!form.subject.trim()) return toast.error("Subject is required");
    try { await api.post("/memos", form); toast.success("Memo posted"); setPanelOpen(false); setForm({ subject: "", body: "", priority: "normal" }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const ack = async (m) => { try { await api.post(`/memos/${m.id}/acknowledge`); load(); } catch (e) { toast.error(apiError(e)); } };
  const remove = async () => {
    try { await api.delete(`/memos/${delRow.id}`); toast.success("Deleted"); setDelRow(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!rows) return <div className="py-12 text-center text-sm text-[#84675B]">Loading memos…</div>;
  return (
    <div data-testid="memos-tab">
      <div className="mb-4 flex items-center justify-between">
        <span className="text-sm text-[#7B6157]">Internal staff memos with acknowledgment tracking</span>
        <Btn onClick={() => setPanelOpen(true)} data-testid="new-memo-btn"><Plus size={15} /> New Memo</Btn>
      </div>
      {rows.length === 0 ? <Empty text="No memos yet." /> : (
        <div className="space-y-2">
          {rows.map((m) => (
            <Card key={m.id} className="p-4" data-testid={`memo-row-${m.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-[#3E2723]">{m.subject}</span>
                    {m.priority === "high" && <span className="rounded-full bg-[#FFEBEE] px-2 py-0.5 text-[10px] font-bold text-[#C62828]">HIGH</span>}
                  </div>
                  {m.body && <div className="mt-1 whitespace-pre-wrap text-sm text-[#5D4037]">{m.body}</div>}
                  <div className="mt-1.5 text-[11px] text-[#84675B]">From {m.from_name} · {dayjs(m.created_at).format("MMM D, YYYY HH:mm")} · {(m.acknowledged_by || []).length} acknowledged</div>
                </div>
                <div className="flex shrink-0 gap-1.5">
                  {m.acknowledged ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#E8F5E9] px-3 py-1 text-xs font-semibold text-[#2E7D32]"><Check size={13} /> Acknowledged</span>
                  ) : (
                    <Btn variant="outline" className="px-3 py-1 text-xs" onClick={() => ack(m)} data-testid={`memo-ack-${m.id}`}>Acknowledge</Btn>
                  )}
                  <button data-testid={`memo-delete-${m.id}`} onClick={() => setDelRow(m)} className="rounded-lg p-1.5 text-[#C62828] hover:bg-[#FFEBEE]"><Trash2 size={15} /></button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
      <SlidePanel open={panelOpen} onClose={() => setPanelOpen(false)} title="New Staff Memo" testId="memo-panel">
        <div className="space-y-4">
          <div><FLabel>Subject *</FLabel><FInput data-testid="memo-subject-input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} /></div>
          <div><FLabel>Body</FLabel><FTextarea data-testid="memo-body-input" rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} /></div>
          <div><FLabel>Priority</FLabel>
            <FSelect data-testid="memo-priority-select" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} placeholderDisabled>
              <option value="normal">Normal</option><option value="high">High</option>
            </FSelect></div>
          <div className="flex justify-end gap-2"><Btn variant="secondary" onClick={() => setPanelOpen(false)}>Cancel</Btn>
            <Btn onClick={create} data-testid="memo-post-btn">Post Memo</Btn></div>
        </div>
      </SlidePanel>
      <ConfirmDialog open={!!delRow} onClose={() => setDelRow(null)} onConfirm={remove} description={`Delete memo "${delRow?.subject}"?`} />
    </div>
  );
}

function ClientNotifyTab({ clients }) {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState({ client_id: "", subject: "", message: "", channel: "portal" });
  const load = () => { api.get("/client-notifications").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e))); };
  useEffect(load, []);
  const send = async () => {
    if (!form.client_id) return toast.error("Select a client");
    if (!form.message.trim()) return toast.error("Message is required");
    try {
      await api.post("/client-notifications", form);
      toast.success("Notification sent to client");
      setForm({ client_id: "", subject: "", message: "", channel: "portal" }); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="grid gap-5 lg:grid-cols-2" data-testid="client-notify-tab">
      <Card className="p-5">
        <h3 className="mb-4 font-heading text-sm font-bold text-[#3E2723]">Send Notification to Client</h3>
        <div className="space-y-3">
          <div><FLabel>Client *</FLabel><ClientSelect clients={clients} value={form.client_id} onChange={(v) => setForm((s) => ({ ...s, client_id: v }))} testId="notify-client-select" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><FLabel>Subject</FLabel><FInput data-testid="notify-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} /></div>
            <div><FLabel>Channel</FLabel>
              <FSelect data-testid="notify-channel" value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} placeholderDisabled>
                <option value="portal">Client Portal</option><option value="sms">SMS (recorded)</option><option value="email">Email (recorded)</option>
              </FSelect></div>
          </div>
          <div><FLabel>Message *</FLabel><FTextarea data-testid="notify-message" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} /></div>
          <div className="flex justify-end"><Btn onClick={send} data-testid="notify-send-btn"><Send size={14} /> Send Notification</Btn></div>
        </div>
      </Card>
      <Card className="p-5">
        <h3 className="mb-4 font-heading text-sm font-bold text-[#3E2723]">Sent Notifications</h3>
        {!rows || rows.length === 0 ? <Empty text="No client notifications sent yet." /> : (
          <div className="space-y-2 max-h-[480px] overflow-y-auto">
            {rows.map((n) => (
              <div key={n.id} className="rounded-xl border border-[#EFEBE0] px-4 py-2.5" data-testid={`notify-row-${n.id}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-[#3E2723]">{n.client_name}</span>
                  <span className="rounded-full bg-[#F5F2EB] px-2 py-0.5 text-[10px] font-bold uppercase text-[#6D4C41]">{n.channel}</span>
                </div>
                {n.subject && <div className="text-xs font-medium text-[#5D4037]">{n.subject}</div>}
                <div className="text-xs text-[#5D4037] line-clamp-2">{n.message}</div>
                <div className="mt-0.5 text-[10px] text-[#84675B]">by {n.sent_by} · {dayjs(n.created_at).format("MMM D, YYYY h:mm A")}</div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

const MERGE_HINT = "Merge fields: {client_name} {first_name} {last_name} {mrn} {dob} {date} {clinic_name} {clinician_name}";

function TemplatesTab({ clients, onGenerated }) {
  const [rows, setRows] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [form, setForm] = useState({ name: "", category: "General", subject: "", body: "" });
  const [genTpl, setGenTpl] = useState(null);
  const [genClient, setGenClient] = useState("");
  const [delRow, setDelRow] = useState(null);
  const load = () => { api.get("/letter-templates").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e))); };
  useEffect(load, []);

  const openEdit = (t) => { setEditRow(t); setForm(t ? { name: t.name, category: t.category, subject: t.subject, body: t.body } : { name: "", category: "General", subject: "", body: "" }); setPanelOpen(true); };
  const save = async () => {
    if (!form.name.trim()) return toast.error("Template name is required");
    try {
      if (editRow) await api.put(`/letter-templates/${editRow.id}`, form);
      else await api.post("/letter-templates", form);
      toast.success("Template saved"); setPanelOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const generate = async () => {
    if (!genClient) return toast.error("Select a client");
    try {
      const r = await api.post("/letters/generate", { template_id: genTpl.id, client_id: genClient });
      toast.success("Letter generated"); setGenTpl(null); setGenClient(""); onGenerated(r.data);
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async () => {
    try { await api.delete(`/letter-templates/${delRow.id}`); toast.success("Deleted"); setDelRow(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!rows) return <div className="py-12 text-center text-sm text-[#84675B]">Loading templates…</div>;
  return (
    <div data-testid="templates-tab">
      <div className="mb-4 flex items-center justify-between">
        <span className="text-sm text-[#7B6157]">{MERGE_HINT}</span>
        <Btn onClick={() => openEdit(null)} data-testid="new-template-btn"><Plus size={15} /> New Template</Btn>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {rows.map((t) => (
          <Card key={t.id} className="p-4" data-testid={`template-card-${t.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="text-sm font-bold text-[#3E2723]">{t.name}</div>
                <div className="text-[11px] text-[#84675B]">{t.category}{t.is_default ? " · System default" : ` · by ${t.created_by}`}</div>
              </div>
              <div className="flex gap-1">
                <button data-testid={`template-edit-${t.id}`} onClick={() => openEdit(t)} className="rounded-lg p-1.5 text-[#6D4C41] hover:bg-[#F5F2EB]"><Pencil size={14} /></button>
                <button data-testid={`template-delete-${t.id}`} onClick={() => setDelRow(t)} className="rounded-lg p-1.5 text-[#C62828] hover:bg-[#FFEBEE]"><Trash2 size={14} /></button>
              </div>
            </div>
            <p className="mt-2 line-clamp-3 whitespace-pre-wrap text-xs text-[#5D4037]">{t.body}</p>
            <Btn variant="outline" className="mt-3 px-3 py-1.5 text-xs" onClick={() => setGenTpl(t)} data-testid={`template-generate-${t.id}`}><FileText size={13} /> Generate Letter</Btn>
          </Card>
        ))}
      </div>
      <SlidePanel open={panelOpen} onClose={() => setPanelOpen(false)} title={editRow ? "Edit Template" : "New Letter Template"} width="max-w-2xl" testId="template-panel">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><FLabel>Name *</FLabel><FInput data-testid="template-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><FLabel>Category</FLabel><FInput data-testid="template-category-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
          </div>
          <div><FLabel>Subject</FLabel><FInput data-testid="template-subject-input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} /></div>
          <div><FLabel>Body</FLabel><FTextarea data-testid="template-body-input" rows={12} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} /></div>
          <p className="text-xs text-[#84675B]">{MERGE_HINT}</p>
          <div className="flex justify-end gap-2"><Btn variant="secondary" onClick={() => setPanelOpen(false)}>Cancel</Btn>
            <Btn onClick={save} data-testid="template-save-btn">Save Template</Btn></div>
        </div>
      </SlidePanel>
      <SlidePanel open={!!genTpl} onClose={() => setGenTpl(null)} title={`Generate: ${genTpl?.name || ""}`} testId="generate-panel">
        <div className="space-y-4">
          <div><FLabel>Client *</FLabel><ClientSelect clients={clients} value={genClient} onChange={setGenClient} testId="generate-client-select" /></div>
          <p className="text-xs text-[#84675B]">Merge fields will be filled from the selected client's chart.</p>
          <div className="flex justify-end gap-2"><Btn variant="secondary" onClick={() => setGenTpl(null)}>Cancel</Btn>
            <Btn onClick={generate} data-testid="generate-confirm-btn"><FileText size={14} /> Generate</Btn></div>
        </div>
      </SlidePanel>
      <ConfirmDialog open={!!delRow} onClose={() => setDelRow(null)} onConfirm={remove} description={`Delete template "${delRow?.name}"?`} />
    </div>
  );
}

export function printLetter(l) {
  openPrintWindow(l.subject || "Letter", `
    <div class="clinic"><h1>${esc(l.client_name ? "" : "")}${esc(l.subject || l.template_name)}</h1>${esc(l.template_name)} · Generated ${dayjs(l.created_at).format("MMMM D, YYYY")}</div>
    <div class="letter-body">${esc(l.content)}</div>
    <div class="meta">Generated by ${esc(l.generated_by)} · Client: ${esc(l.client_name)} · CONFIDENTIAL - Protected Health Information</div>`);
}

function LettersTab({ refreshKey }) {
  const [rows, setRows] = useState(null);
  const [viewRow, setViewRow] = useState(null);
  const [delRow, setDelRow] = useState(null);
  const load = () => { api.get("/letters").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e))); };
  useEffect(load, [refreshKey]);
  const remove = async () => {
    try { await api.delete(`/letters/${delRow.id}`); toast.success("Deleted"); setDelRow(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (!rows) return <div className="py-12 text-center text-sm text-[#84675B]">Loading letters…</div>;
  return (
    <div data-testid="letters-tab">
      {rows.length === 0 ? <Empty text="No letters generated yet. Use a template to create one." /> : (
        <Card className="overflow-hidden">
          <Table headers={["Date", "Client", "Template", "Subject", ""]}>
            {rows.map((l) => (
              <tr key={l.id} className="border-t border-[#F1EEE7] hover:bg-[#FDFCF8]" data-testid={`letter-row-${l.id}`}>
                <Td>{dayjs(l.created_at).format("MM/DD/YYYY")}</Td>
                <Td className="font-medium">{l.client_name}</Td>
                <Td>{l.template_name}</Td>
                <Td className="max-w-[280px] truncate">{l.subject}</Td>
                <Td>
                  <div className="flex gap-1">
                    <button data-testid={`letter-view-${l.id}`} onClick={() => setViewRow(l)} className="rounded-lg p-1.5 text-[#6D4C41] hover:bg-[#F5F2EB]"><FileText size={15} /></button>
                    <button data-testid={`letter-print-${l.id}`} onClick={() => printLetter(l)} className="rounded-lg p-1.5 text-[#6D4C41] hover:bg-[#F5F2EB]"><Printer size={15} /></button>
                    <button data-testid={`letter-delete-${l.id}`} onClick={() => setDelRow(l)} className="rounded-lg p-1.5 text-[#C62828] hover:bg-[#FFEBEE]"><Trash2 size={15} /></button>
                  </div>
                </Td>
              </tr>
            ))}
          </Table>
        </Card>
      )}
      <SlidePanel open={!!viewRow} onClose={() => setViewRow(null)} title={viewRow?.subject || "Letter"} subtitle={viewRow?.client_name} width="max-w-2xl" testId="letter-view-panel">
        {viewRow && (
          <div className="space-y-4">
            <div className="whitespace-pre-wrap rounded-xl bg-[#FDFCF8] border border-[#EFEBE0] p-5 text-sm text-[#3E2723]" data-testid="letter-content">{viewRow.content}</div>
            <div className="flex justify-end"><Btn onClick={() => printLetter(viewRow)} data-testid="letter-view-print-btn"><Printer size={14} /> Print / Save PDF</Btn></div>
          </div>
        )}
      </SlidePanel>
      <ConfirmDialog open={!!delRow} onClose={() => setDelRow(null)} onConfirm={remove} description={`Delete this letter for ${delRow?.client_name}?`} />
    </div>
  );
}

export function printFaxCover(f) {
  openPrintWindow("Fax Cover Sheet", `
    <div class="clinic"><h1>FAX COVER SHEET</h1>${esc(f.clinic_name)} · ${dayjs(f.created_at).format("MMMM D, YYYY HH:mm")}</div>
    ${f.urgent ? '<div class="banner">⚠ URGENT</div>' : ""}
    <div class="row">
      <div><div class="label">To</div><div class="value">${esc(f.to_name)}${f.to_org ? " - " + esc(f.to_org) : ""}</div>
        <div class="label">Fax</div><div class="value">${esc(f.to_fax) || "-"}</div></div>
      <div><div class="label">From</div><div class="value">${esc(f.from_name)} - ${esc(f.clinic_name)}</div>
        <div class="label">Fax</div><div class="value">${esc(f.from_fax) || "-"}</div></div>
    </div>
    <div class="label">Re</div><div class="value">${esc(f.re) || "-"}</div>
    <div class="label">Pages (including cover)</div><div class="value">${f.pages}</div>
    ${f.notes ? `<div class="label">Notes</div><div class="value" style="white-space:pre-wrap">${esc(f.notes)}</div>` : ""}
    <div class="banner">CONFIDENTIALITY NOTICE</div>
    <p style="font-size:11px;color:#5D4037">This facsimile contains confidential health information protected by federal and state law (HIPAA). It is intended only for the use of the individual or entity named above. If you are not the intended recipient, you are hereby notified that any disclosure, copying, distribution, or action taken in reliance on the contents is strictly prohibited. If you received this in error, please notify the sender immediately and destroy all copies.</p>`);
}

function FaxTab() {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState({ to_name: "", to_org: "", to_fax: "", from_fax: "", re: "", pages: "1", urgent: false, notes: "" });
  const [delRow, setDelRow] = useState(null);
  const load = () => { api.get("/fax-covers").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e))); };
  useEffect(load, []);
  const f = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const create = async (print) => {
    if (!form.to_name.trim()) return toast.error("Recipient name is required");
    try {
      const r = await api.post("/fax-covers", form);
      toast.success("Fax cover saved");
      setForm({ to_name: "", to_org: "", to_fax: "", from_fax: "", re: "", pages: "1", urgent: false, notes: "" });
      load();
      if (print) printFaxCover(r.data);
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async () => {
    try { await api.delete(`/fax-covers/${delRow.id}`); toast.success("Deleted"); setDelRow(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="grid gap-5 lg:grid-cols-2" data-testid="fax-tab">
      <Card className="p-5">
        <h3 className="mb-4 font-heading text-sm font-bold text-[#3E2723]">New Fax Cover Sheet</h3>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><FLabel>To (name) *</FLabel><FInput data-testid="fax-to-name" value={form.to_name} onChange={f("to_name")} /></div>
            <div><FLabel>Organization</FLabel><FInput data-testid="fax-to-org" value={form.to_org} onChange={f("to_org")} /></div>
            <div><FLabel>To fax #</FLabel><FInput data-testid="fax-to-fax" value={form.to_fax} onChange={f("to_fax")} /></div>
            <div><FLabel>From fax #</FLabel><FInput data-testid="fax-from-fax" value={form.from_fax} onChange={f("from_fax")} /></div>
            <div><FLabel>Re</FLabel><FInput data-testid="fax-re" value={form.re} onChange={f("re")} /></div>
            <div><FLabel>Pages (incl. cover)</FLabel><FInput data-testid="fax-pages" type="number" min="1" value={form.pages} onChange={f("pages")} /></div>
          </div>
          <CheckBox testId="fax-urgent-check" checked={form.urgent} onChange={() => setForm((s) => ({ ...s, urgent: !s.urgent }))} label="Mark as URGENT" />
          <div><FLabel>Notes</FLabel><FTextarea data-testid="fax-notes" rows={3} value={form.notes} onChange={f("notes")} /></div>
          <div className="flex justify-end gap-2">
            <Btn variant="secondary" onClick={() => create(false)} data-testid="fax-save-btn">Save</Btn>
            <Btn onClick={() => create(true)} data-testid="fax-save-print-btn"><Printer size={14} /> Save & Print</Btn>
          </div>
        </div>
      </Card>
      <Card className="p-5">
        <h3 className="mb-4 font-heading text-sm font-bold text-[#3E2723]">Recent Cover Sheets</h3>
        {!rows || rows.length === 0 ? <Empty text="No fax covers yet." /> : (
          <div className="space-y-2">
            {rows.map((x) => (
              <div key={x.id} className="flex items-center justify-between rounded-xl border border-[#EFEBE0] px-4 py-2.5" data-testid={`fax-row-${x.id}`}>
                <div>
                  <div className="text-sm font-semibold text-[#3E2723]">{x.to_name}{x.urgent && <span className="ml-2 rounded-full bg-[#FFEBEE] px-2 py-0.5 text-[10px] font-bold text-[#C62828]">URGENT</span>}</div>
                  <div className="text-[11px] text-[#84675B]">{x.re || "-"} · {x.pages} pg · {dayjs(x.created_at).format("MMM D, HH:mm")}</div>
                </div>
                <div className="flex gap-1">
                  <button data-testid={`fax-print-${x.id}`} onClick={() => printFaxCover(x)} className="rounded-lg p-1.5 text-[#6D4C41] hover:bg-[#F5F2EB]"><Printer size={15} /></button>
                  <button data-testid={`fax-delete-${x.id}`} onClick={() => setDelRow(x)} className="rounded-lg p-1.5 text-[#C62828] hover:bg-[#FFEBEE]"><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
      <ConfirmDialog open={!!delRow} onClose={() => setDelRow(null)} onConfirm={remove} description={`Delete fax cover for ${delRow?.to_name}?`} />
    </div>
  );
}

export default function LettersMessaging() {
  const [tab, setTab] = useState("alerts");
  const [refreshKey, setRefreshKey] = useState(0);
  const clients = useClients();
  return (
    <div data-testid="letters-messaging-page">
      <PageHeader title="Letters & Messaging" subtitle="Alerts, staff memos, letter templates with merge fields & fax cover sheets" />
      <div className="mb-5 flex gap-2 overflow-x-auto">
        {TABS.map(([k, l]) => (
          <button key={k} data-testid={`lm-tab-${k}`} onClick={() => setTab(k)}
            className={`whitespace-nowrap rounded-full px-5 py-2 text-sm font-semibold transition-colors ${tab === k ? "bg-[#4E342E] text-white" : "bg-[#F5F2EB] text-[#6D4C41] hover:bg-[#EDE7DC]"}`}>{l}</button>
        ))}
      </div>
      {tab === "alerts" && <AlertsTab />}
      {tab === "notify" && <ClientNotifyTab clients={clients} />}
      {tab === "memos" && <MemosTab />}
      {tab === "templates" && <TemplatesTab clients={clients} onGenerated={() => { setRefreshKey((k) => k + 1); setTab("letters"); }} />}
      {tab === "letters" && <LettersTab refreshKey={refreshKey} />}
      {tab === "fax" && <FaxTab />}
    </div>
  );
}
